import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const StripeIntegration = () => {
  const [stripeStatus, setStripeStatus] = useState({
    accountId: '',
    chargesEnabled: false,
    transfersEnabled: false,
    onboardingComplete: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get('/api/payments/stripe');
      setStripeStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch Stripe status');
    }
  };

  const handleStartOnboarding = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/payments/stripe/setup');
      toast.success('Stripe account created');
      // In production, redirect to Stripe onboarding URL
      // window.open(response.data.onboardingUrl, '_blank');
      setStripeStatus(prev => ({ ...prev, accountId: response.data.accountId }));
    } catch (error) {
      toast.error('Failed to setup Stripe account');
    } finally {
      setLoading(false);
    }
  };

  const handleFinishOnboarding = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/payments/stripe/finish');
      toast.success('Stripe onboarding completed');
      setStripeStatus({
        accountId: response.data.accountId,
        chargesEnabled: response.data.chargesEnabled,
        transfersEnabled: response.data.transfersEnabled,
        onboardingComplete: response.data.onboardingComplete
      });
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (step) => {
    switch (step) {
      case 1:
        return stripeStatus.accountId ? 'complete' : 'current';
      case 2:
        return stripeStatus.accountId && (stripeStatus.chargesEnabled || stripeStatus.transfersEnabled) ? 'complete' : stripeStatus.accountId ? 'current' : 'pending';
      case 3:
        return stripeStatus.onboardingComplete ? 'complete' : stripeStatus.accountId ? 'current' : 'pending';
      default:
        return 'pending';
    }
  };

  const StepIndicator = ({ step, title, status }) => (
    <div className="flex items-center space-x-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
        status === 'complete' ? 'bg-green-100 text-green-800' :
        status === 'current' ? 'bg-blue-100 text-blue-800' :
        'bg-gray-100 text-gray-500'
      }`}>
        {status === 'complete' ? '✓' : step}
      </div>
      <span className={`${
        status === 'complete' ? 'text-green-800' :
        status === 'current' ? 'text-blue-800' :
        'text-gray-500'
      }`}>
        {title}
      </span>
    </div>
  );

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Stripe Integration</h2>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Onboarding Steps */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Onboarding Progress</h3>
          <div className="space-y-4">
            <StepIndicator step={1} title="Setup" status={getStepStatus(1)} />
            <StepIndicator step={2} title="Verification" status={getStepStatus(2)} />
            <StepIndicator step={3} title="Complete" status={getStepStatus(3)} />
          </div>
        </div>

        {/* Account Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Account Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">Account ID</span>
              <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {stripeStatus.accountId || 'Not connected'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">Charges Enabled</span>
              <div className="flex items-center">
                {stripeStatus.chargesEnabled ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span className={`ml-2 text-sm ${
                  stripeStatus.chargesEnabled ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stripeStatus.chargesEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">Transfers Enabled</span>
              <div className="flex items-center">
                {stripeStatus.transfersEnabled ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span className={`ml-2 text-sm ${
                  stripeStatus.transfersEnabled ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stripeStatus.transfersEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t pt-6">
          {!stripeStatus.accountId && (
            <button
              onClick={handleStartOnboarding}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Setting up...' : 'Start Stripe Setup'}
            </button>
          )}
          
          {stripeStatus.accountId && !stripeStatus.onboardingComplete && (
            <button
              onClick={handleFinishOnboarding}
              disabled={loading}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Finish Onboarding'}
            </button>
          )}
          
          {stripeStatus.onboardingComplete && (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-600 font-medium">Stripe integration complete!</p>
              <p className="text-sm text-gray-500 mt-1">
                You can now accept payments through Stripe.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeIntegration;